import { SqlComponent } from "./SqlComponent";
import { IdentifierString, ValueList, ValueComponent } from "./ValueComponent";
import { SetClause, SetClauseItem, SourceExpression, WhereClause, WithClause } from "./Clause";

export type MergeMatchType = "matched" | "not_matched" | "not_matched_by_source" | "not_matched_by_target";

export abstract class MergeAction extends SqlComponent {
    static kind = Symbol("MergeAction");
}

export class MergeUpdateAction extends MergeAction {
    static kind = Symbol("MergeUpdateAction");
    setClause: SetClause;
    whereClause: WhereClause | null;

    constructor(setClause: SetClause | SetClauseItem[], whereClause?: WhereClause | null) {
        super();
        this.setClause = setClause instanceof SetClause ? setClause : new SetClause(setClause);
        this.whereClause = whereClause ?? null;
    }
}

export class MergeDeleteAction extends MergeAction {
    static kind = Symbol("MergeDeleteAction");
    whereClause: WhereClause | null;

    constructor(whereClause?: WhereClause | null) {
        super();
        this.whereClause = whereClause ?? null;
    }
}

export class MergeInsertAction extends MergeAction {
    static kind = Symbol("MergeInsertAction");
    columns: IdentifierString[] | null;
    values: ValueList | null;
    defaultValues: boolean;
    private valuesLeadingComments: string[] | null;

    constructor(params: {
        columns?: (IdentifierString | string)[] | null;
        values?: ValueList | null;
        defaultValues?: boolean;
        valuesLeadingComments?: string[] | null;
    }) {
        super();
        this.columns = params.columns
            ? params.columns.map(col => (typeof col === "string" ? new IdentifierString(col) : col))
            : null;
        this.values = params.values ?? null;
        this.defaultValues = params.defaultValues ?? false;
        this.valuesLeadingComments = params.valuesLeadingComments ? [...params.valuesLeadingComments] : null;
    }

    addValuesLeadingComments(comments: string[]): void {
        if (!comments || comments.length === 0) {
            return;
        }
        if (!this.valuesLeadingComments) {
            this.valuesLeadingComments = [];
        }
        for (const comment of comments) {
            if (!this.valuesLeadingComments.includes(comment)) {
                this.valuesLeadingComments.push(comment);
            }
        }
    }

    getValuesLeadingComments(): string[] {
        return this.valuesLeadingComments ? [...this.valuesLeadingComments] : [];
    }
}

export class MergeDoNothingAction extends MergeAction {
    static kind = Symbol("MergeDoNothingAction");
}

export class MergeWhenClause extends SqlComponent {
    static kind = Symbol("MergeWhenClause");
    matchType: MergeMatchType;
    condition: ValueComponent | null;
    action: MergeAction;
    private thenLeadingComments: string[] | null;

    constructor(
        matchType: MergeMatchType,
        action: MergeAction,
        condition?: ValueComponent | null,
        options?: { thenLeadingComments?: string[] | null }
    ) {
        super();
        this.matchType = matchType;
        this.action = action;
        this.condition = condition ?? null;
        this.thenLeadingComments = options?.thenLeadingComments
            ? [...options.thenLeadingComments]
            : null;
    }

    addThenLeadingComments(comments: string[]): void {
        if (!comments || comments.length === 0) {
            return;
        }
        if (!this.thenLeadingComments) {
            this.thenLeadingComments = [];
        }
        for (const comment of comments) {
            if (!this.thenLeadingComments.includes(comment)) {
                this.thenLeadingComments.push(comment);
            }
        }
    }

    getThenLeadingComments(): string[] {
        return this.thenLeadingComments ? [...this.thenLeadingComments] : [];
    }
}

export class MergeQuery extends SqlComponent {
    static kind = Symbol("MergeQuery");
    withClause: WithClause | null;
    target: SourceExpression;
    source: SourceExpression;
    onCondition: ValueComponent;
    whenClauses: MergeWhenClause[];

    constructor(params: {
        withClause?: WithClause | null;
        target: SourceExpression;
        source: SourceExpression;
        onCondition: ValueComponent;
        whenClauses: MergeWhenClause[];
    }) {
        super();
        this.withClause = params.withClause ?? null;
        this.target = params.target;
        this.source = params.source;
        this.onCondition = params.onCondition;
        this.whenClauses = params.whenClauses;
    }
}
